import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined } from "@ant-design/icons";
import HistoryUploadedDocument from "./HistoryUploadedDocument";
import { downloadDocument } from "../../../../apis/patientService";

interface RawHistoryItem {
  id: number;
  roundNumber: number;
  workflowAction: string;
  createdAt: string;
  payloadJson: string;
  createdByUserId: number;
}

interface ParsedHistoryItem {
  key: number;
  roundNumber: number;
  action: string;
  createdAt: string;
  query?: string;
  response?: string;
  status?: boolean;
  state?: string;
  responseTime?: string;
  stage: any;
}

interface HistoryModalProps {
  visible: boolean;
  onClose: () => void;
  data: RawHistoryItem[];
  isLoading: boolean;
}

const HistoryModal: React.FC<HistoryModalProps> = ({
  visible,
  onClose,
  data,
  isLoading,
}) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const [uploadedDocument, setUploadedDocument] = useState<any>([]);
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [documentName, setDocumentName] = useState("");

  useEffect(() => {
    if (!visible) {
      setPagination({
        current: 1,
        pageSize: 100,
      });
    }
  }, [visible]);

  const parsedData: ParsedHistoryItem[] = data?.map((item) => {
  let payload: any = {};

  try {
    payload = JSON.parse(item.payloadJson);
  } catch (e) {
    console.error("Invalid JSON:", e);
  }

  const queryData = payload?.query || {};

  return {
    key: item.id,
    roundNumber: item.roundNumber,
    action: item.workflowAction,
    createdAt: item.createdAt,

    query: queryData.queryQuestion || queryData.receivedQuery,
    response: queryData.returnedResponse,
    status: queryData.status,
    state: queryData.stateName,
    responseTime: queryData.responseDateTime,
    stage: queryData.queryStage,

    returnedDocumentUrl: payload?.returnedDocumentUrl
  };
});

  const columns: ColumnsType<ParsedHistoryItem> = [
    {
      title: "Round",
      dataIndex: "roundNumber",
      width: 80,
    },
    {
      title: "Query",
      dataIndex: "query",

      render: (text?: string) =>
        text ? <Tooltip title={text}>{text}</Tooltip> : "-",
      width: 500
    },
    {
      title: "Response",
      dataIndex: "response",
      ellipsis: { showTitle: false },
      render: (text?: string) =>
        text ? <Tooltip title={text}>{text}</Tooltip> : "-",
      width: 400
    },
    {
      title: "Uploaded Document",
      dataIndex: "uploadedDocument",
      ellipsis: { showTitle: false },
      render: (text: string, record: any) =>
        record?.returnedDocumentUrl?.length > 0 ? (
          <EyeOutlined
            onClick={() => {
                setPreviewDocument(true);
                setUploadedDocument(record?.returnedDocumentUrl);
            }}
          />
        ) : (
          "-"
        ),
        width: 180
    },
    {
      title: "State",
      dataIndex: "state",
      render: (text?: string) => (text ? <Tag color="green">{text}</Tag> : "-"),
      width: 170
    },
    {
      title: "Response Date/Time",
      dataIndex: "responseTime",
      width: 200
    },
    {
      title: "Stage",
      dataIndex: "stage",
      render: (text: string) => <Tag color="blue">{text}</Tag>,
      width: 110
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string) => <Tag color="pink">{text}</Tag>,
      width: 120
    },
  ];

    const handleDownloadDocument = async (
          fileName: string = "Document",
          url: string,
        ) => {
          try {
            await downloadDocument(fileName, url);
          } catch (err) {
            if (url) {
            } else {
            }
          }
        };

  return (
    <>
      <Modal
        title={<span style={{ color: "#00549e", fontWeight: 600 }}>Query History</span>}
        open={visible}
        onCancel={onClose}
        footer={null}
        width="100vw"
        style={{
          top: 10,
          left: 4,
          padding: 0,
          maxWidth: "100vw",
          height: "135vh",
        }}
        wrapClassName="operational-table-modal"
        bodyStyle={{
          height: "calc(126vh - 55px)",
          overflow: "auto",
          padding: 0,
        }}
        loading={isLoading}
        destroyOnClose
      >
        <Table
          columns={columns}
          dataSource={parsedData}
          tableLayout="fixed"
          className="operational-dashboard-table"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          bordered
          rowClassName={(record, index) =>
            index % 2 === 0 ? "ant-table-row-light" : ""
          }
          scroll={{
            x: 150,
            y: 670,
          }}
        />
      </Modal>

      <HistoryUploadedDocument
        handleCancel={() => setPreviewDocument(false)}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        // handleSetDocumentUrl={handleSetDocumentUrl}
        documents={uploadedDocument}
        setDocumentName={setDocumentName}
      />
    </>
  );
};

export default HistoryModal;
